import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UserAvatarProps {
  user?: {
    name?: string;
    email?: string;
    profilePicture?: string;
    profileImage?: string;
  } | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showProfilePicture?: boolean;
  useFallbackService?: boolean;
  /** Show a guest indicator badge on the avatar */
  isGuest?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'md',
  className = '',
  showProfilePicture = true,
  useFallbackService = true,
  isGuest = false
}) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  // Guest indicator size based on avatar size
  const guestIndicatorClasses = {
    sm: 'w-3 h-3 -bottom-0.5 -right-0.5',
    md: 'w-4 h-4 -bottom-0.5 -right-0.5',
    lg: 'w-5 h-5 -bottom-1 -right-1'
  };

  const getInitials = (user: UserAvatarProps['user']) => {
    const name = user?.name || user?.email || 'User';

    // Extract initials from name or email
    if (name.includes('@')) {
      // For email, take first two letters before @
      return name.substring(0, 2).toUpperCase();
    } else {
      // For name, take first letter of first and last name
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      } else {
        return name.substring(0, 2).toUpperCase();
      }
    }
  };

  const getFallbackImage = () => {
    const userName = user?.name || user?.email || 'User';
    const sizeMap = { sm: 24, md: 32, lg: 48 };
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=276891&color=ffffff&size=${sizeMap[size]}`;
  };

  const profileImage = user?.profilePicture || user?.profileImage;
  const hasValidImage = showProfilePicture && profileImage && profileImage.trim() !== '' && !imageError;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gray-200`}>
        {hasValidImage ? (
          <img
            src={profileImage}
            alt={user?.name || user?.email || 'User'}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        ) : useFallbackService && showProfilePicture ? (
          <img
            src={getFallbackImage()}
            alt={user?.name || user?.email || 'User'}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-primary-600">
            <span className="text-white font-medium">
              {getInitials(user)}
            </span>
          </div>
        )}
      </div>
      {/* Guest indicator badge */}
      {isGuest && (
        <div
          className={`absolute ${guestIndicatorClasses[size]} bg-gray-400 rounded-full flex items-center justify-center ring-2 ring-white`}
          title={t('profile:organizationMembers.roles.guest', 'Guest')}
        >
          <svg className="w-2/3 h-2/3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;